import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Seo from "@/components/Seo";
import ListingCards from "@/components/ListingCards";
import SoldShowcase from "@/components/SoldShowcase";
import SearchPanel from "@/components/SearchPanel";
import { Field, inputCls, SmsConsent, useLeadSubmit } from "@/components/shared";
import { FadeUp } from "@/components/Reveal";
import { Eyebrow, LuxeButton } from "@/components/shared";

const TABS = [
  { id: "newest", label: "Newest Listings" },
  { id: "sold", label: "Recently Sold" },
  { id: "vacation", label: "Vacation Homes" },
  { id: "dr", label: "Dominican Republic" },
];

const ListingAlerts = () => {
  const { submit, submitting } = useLeadSubmit("contact");
  const [form, setForm] = useState({ first_name: "", email: "", phone: "" });
  const [consent, setConsent] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    const ok = await submit({
      first_name: form.first_name,
      last_name: "",
      email: form.email,
      phone: form.phone,
      message: "Please add me to new-listing alerts.",
      sms_consent: consent,
      details: { interest: "Listing Alerts" },
    });
    if (ok) {
      setForm({ first_name: "", email: "", phone: "" });
      setConsent(false);
    }
  };

  return (
    <form onSubmit={handle} className="space-y-5" data-testid="listing-alerts-form">
      <Field label="First Name *" htmlFor="alerts-name">
        <input id="alerts-name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className={inputCls} placeholder="First name" data-testid="alerts-name-input" />
      </Field>
      <Field label="Email *" htmlFor="alerts-email">
        <input id="alerts-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" data-testid="alerts-email-input" />
      </Field>
      <Field label="Phone" htmlFor="alerts-phone">
        <input id="alerts-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="(___) ___-____" data-testid="alerts-phone-input" />
      </Field>
      <SmsConsent checked={consent} onChange={setConsent} id="alerts-sms-consent" />
      <button
        type="submit"
        disabled={submitting}
        data-testid="alerts-submit-button"
        className="inline-flex w-full items-center justify-center gap-3 bg-charcoal px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-obsidian disabled:opacity-60"
      >
        {submitting ? "Signing Up…" : "Sign Me Up"}
      </button>
    </form>
  );
};

const DR_IMG = "https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/7-1920w.png";

const Listings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const [tab, setTabState] = useState(TABS.some((t) => t.id === tabParam) ? tabParam : "newest");

  useEffect(() => {
    if (TABS.some((t) => t.id === tabParam)) setTabState(tabParam);
  }, [tabParam]);

  const setTab = (id) => {
    setTabState(id);
    setSearchParams(id === "newest" ? {} : { tab: id }, { replace: true });
  };

  const query = {
    q: searchParams.get("q") || "",
    min: searchParams.get("min") || "",
    max: searchParams.get("max") || "",
    beds: searchParams.get("beds") || "",
  };
  const hasQuery = Boolean(query.q || query.min || query.max || query.beds);

  return (
    <>
      <Seo
        title="Our Listings | Luxury Homes in DC, Maryland & Virginia — Scherif Real Estate"
        siteName="Scherif Real Estate"
        description="Search live luxury listings across Washington DC, Maryland, and Northern Virginia — plus vacation homes and Dominican Republic properties."
      />

      <section className="bg-stone-beige pb-16 pt-36 sm:pt-44" data-testid="listings-header">
        <div className="container-luxe">
          <FadeUp>
            <Eyebrow className="mb-4">The Portfolio</Eyebrow>
            <h1 className="font-serif text-5xl font-light tracking-tight text-charcoal sm:text-6xl">Our Listings</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-muted">
              Live from the MLS — exceptional properties across Washington, DC, Maryland, and Virginia, with an international portfolio in the
              Dominican Republic.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="mt-12">
            <div className="flex flex-wrap gap-2 border-b border-charcoal/10" role="tablist" aria-label="Listing categories">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`listings-tab-${t.id}`}
                  className={`relative px-5 py-4 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                    tab === t.id ? "text-charcoal" : "text-slate-muted/70 hover:text-charcoal"
                  }`}
                >
                  {t.label}
                  {tab === t.id && <motion.span layoutId="tab-underline" className="absolute inset-x-4 bottom-0 h-px bg-champagne" />}
                </button>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="container-luxe py-16 sm:py-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.25, 1, 0.35, 1] }}
          >
            {tab === "newest" && (
              <div data-testid="newest-listings-panel">
                <div className="mb-14">
                  <ListingCards
                    kind="featured"
                    limit={24}
                    heading={hasQuery ? "Matching Listings — Live From the MLS" : "Featured Now — Live From the MLS"}
                    query={query}
                    emptyNode={
                      <div className="border border-warm-border bg-stone-beige p-10 text-center" data-testid="search-empty">
                        <p className="font-serif text-2xl font-normal text-charcoal">No current listings match that search.</p>
                        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-muted">
                          Our full-MLS access goes far beyond what&apos;s shown here — tell us your criteria and we&apos;ll search everything, including quiet and off-market opportunities.
                        </p>
                        <div className="mt-6">
                          <LuxeButton to="/contact" variant="dark" testId="search-empty-contact-btn">Tell Us What You Need</LuxeButton>
                        </div>
                      </div>
                    }
                  />
                </div>
              <div className="grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <h2 className="mb-8 font-serif text-3xl font-light text-charcoal sm:text-4xl">Newest Listings</h2>
                  <div className="border border-warm-border bg-white p-6 shadow-[0_20px_60px_rgba(0,0,0,0.05)] sm:p-8">
                    <SearchPanel testIdPrefix="listings-search" />
                  </div>
                </div>
                <aside className="lg:col-span-5">
                  <div className="border border-warm-border bg-stone-beige p-6 sm:p-8">
                    <h3 className="font-serif text-2xl font-light text-charcoal">Never Miss a Listing</h3>
                    <p className="mb-6 mt-3 text-sm leading-relaxed text-slate-muted">
                      Create a free account to save searches and receive new-listing alerts.
                    </p>
                    <ListingAlerts />
                  </div>
                </aside>
              </div>
              </div>
            )}

            {tab === "sold" && (
              <div data-testid="recently-sold-panel">
                <SoldShowcase limit={8} />
              <div className="grid items-center gap-12 lg:grid-cols-12">
                <div className="lg:col-span-6">
                  <h2 className="font-serif text-3xl font-light text-charcoal sm:text-4xl">Recently Sold</h2>
                  <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-muted">
                    Every closing tells a story of strategy, positioning, and negotiation. Our sold portfolio is available on request — and current
                    market activity can be browsed live through our MLS portal.
                  </p>
                  <div className="mt-8">
                    <LuxeButton to="/contact" variant="outline-dark" testId="sold-request-btn">Request Sold Portfolio</LuxeButton>
                  </div>
                </div>
                <FadeUp className="lg:col-span-6">
                  <div className="img-zoom">
                    <img
                      src="https://images.unsplash.com/photo-1762811054947-605b20298615?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHx8bHV4dXJ5JTIwaG91c2V8ZW58MHx8fHwxNzg4OTYxMDI4fDA&ixlib=rb-4.1.0&q=85"
                      alt="Recently sold luxury homes in the Washington DC region"
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </FadeUp>
              </div>
              </div>
            )}

            {tab === "vacation" && (
              <div className="grid items-center gap-12 lg:grid-cols-12" data-testid="vacation-listings-panel">
                <FadeUp className="order-2 lg:order-1 lg:col-span-6">
                  <div className="img-zoom">
                    <img src={DR_IMG} alt="Luxury Dominican Republic villa with private pool" className="aspect-[4/3] w-full object-cover" loading="lazy" />
                  </div>
                </FadeUp>
                <div className="order-1 lg:order-2 lg:col-span-6">
                  <h2 className="font-serif text-3xl font-light text-charcoal sm:text-4xl">Vacation Homes</h2>
                  <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-muted">
                    From the beaches of the Dominican Republic to destinations beyond, explore private retreats curated by Scherif Real Estate —
                    designed for unforgettable stays.
                  </p>
                  <div className="mt-8">
                    <LuxeButton to="/vacation-homes" variant="dark" testId="vacation-explore-btn">Explore Vacation Homes</LuxeButton>
                  </div>
                </div>
              </div>
            )}

            {tab === "dr" && (
              <div className="grid items-center gap-12 lg:grid-cols-12" data-testid="dr-listings-panel">
                <div className="lg:col-span-6">
                  <h2 className="font-serif text-3xl font-light text-charcoal sm:text-4xl">Dominican Republic</h2>
                  <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-muted">
                    Our international portfolio — luxury villas, private retreats, and investment opportunities across Cap Cana, Punta Cana, and
                    beyond.
                  </p>
                  <div className="mt-8">
                    <LuxeButton to="/dominican-republic" variant="dark" testId="dr-explore-btn">Explore the DR Portfolio</LuxeButton>
                  </div>
                </div>
                <FadeUp className="lg:col-span-6">
                  <div className="img-zoom">
                    <img
                      src="https://irp.cdn-website.com/90d2d2b8/dms3rep/multi/opt/IMG_5529-5c02de18-1920w.webp"
                      alt="Dominican Republic luxury destination"
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                </FadeUp>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>
    </>
  );
};

export default Listings;
